import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../../components/ui/tabs';
import ClientEngagementTab from '../../components/customer-success/ClientEngagementTab';
import NpsCockpitPage from './NpsCockpitPage';
import {
  RefreshCw, AlertTriangle, TrendingDown, Frown, Clock, Banknote,
  Users, Trophy, Heart, Activity, Search, ArrowUpDown,
  Settings, Play, CheckCircle2, XCircle, Eye, FileDown, LayoutDashboard,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_STYLES = {
  champion: { color: 'bg-emerald-50 text-emerald-700 border-emerald-200', emoji: '🟢' },
  engaged: { color: 'bg-blue-50 text-blue-700 border-blue-200', emoji: '🔵' },
  at_risk: { color: 'bg-amber-50 text-amber-700 border-amber-200', emoji: '🟡' },
  dormant: { color: 'bg-orange-50 text-orange-700 border-orange-200', emoji: '🟠' },
  churning: { color: 'bg-red-50 text-red-700 border-red-200', emoji: '🔴' },
};

const ALERT_META = {
  drop_velocity: { icon: TrendingDown, label: 'Volume en chute', color: 'text-red-600 bg-red-50' },
  detractor_nps: { icon: Frown, label: 'Détracteurs NPS', color: 'text-rose-600 bg-rose-50' },
  strategic_inactive: { icon: Clock, label: 'Stratégiques dormants', color: 'text-amber-600 bg-amber-50' },
  late_payer_recurring: { icon: Banknote, label: 'Retards de paiement', color: 'text-orange-600 bg-orange-50' },
};

const fmtFcfa = (n) => {
  if (!n) return '0';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}k`;
  return n.toString();
};

export default function CustomerSuccessPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'heart';
  const [activeTab, setActiveTab] = useState(initialTab);

  const handleTabChange = (v) => {
    setActiveTab(v);
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (v === 'heart') p.delete('tab'); else p.set('tab', v);
      return p;
    });
  };

  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [snapshotAt, setSnapshotAt] = useState(null);
  const [source, setSource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tierFilter, setTierFilter] = useState('all');
  const [sort, setSort] = useState('score_desc');
  const [search, setSearch] = useState('');

  // Phase 2 — scheduler config
  const [schedulerCfg, setSchedulerCfg] = useState(null);
  const [intervalDraft, setIntervalDraft] = useState(6);
  const [savingCfg, setSavingCfg] = useState(false);
  const [runningNow, setRunningNow] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [s, c, sc] = await Promise.all([
        axios.get(`${API}/admin/customer-success/stats`),
        axios.get(`${API}/admin/customer-success/clients`, {
          params: { tier: tierFilter, sort, limit: 500 },
        }),
        axios.get(`${API}/admin/customer-success/scheduler/config`),
      ]);
      setStats(s.data);
      setClients(c.data.clients || []);
      setSnapshotAt(c.data.snapshotAt || null);
      setSource(c.data.source || null);
      setSchedulerCfg(sc.data);
      setIntervalDraft(sc.data?.intervalHours ?? 6);
    } catch (e) {
      console.error('CS fetch error:', e);
    } finally {
      setLoading(false);
    }
  };

  const saveSchedulerConfig = async () => {
    setSavingCfg(true);
    try {
      const { data } = await axios.put(
        `${API}/admin/customer-success/scheduler/config`,
        { intervalHours: Number(intervalDraft) }
      );
      setSchedulerCfg(data);
    } catch (e) {
      console.error('save scheduler cfg error:', e);
      alert(e.response?.data?.detail || 'Erreur lors de la sauvegarde.');
    } finally {
      setSavingCfg(false);
    }
  };

  const toggleSchedulerEnabled = async (enabled) => {
    setSavingCfg(true);
    try {
      const { data } = await axios.put(
        `${API}/admin/customer-success/scheduler/config`,
        { enabled }
      );
      setSchedulerCfg(data);
    } catch (e) {
      console.error('toggle scheduler error:', e);
      alert(e.response?.data?.detail || 'Erreur.');
    } finally {
      setSavingCfg(false);
    }
  };

  const runNow = async () => {
    setRunningNow(true);
    try {
      await axios.post(`${API}/admin/customer-success/scheduler/run-now`);
      await fetchAll();
    } catch (e) {
      console.error('run-now error:', e);
      alert(e.response?.data?.detail || 'Erreur lors du recalcul.');
    } finally {
      setRunningNow(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tierFilter, sort]);

  const filteredClients = useMemo(() => {
    if (!search) return clients;
    const q = search.toLowerCase();
    return clients.filter(
      (c) =>
        c.companyName.toLowerCase().includes(q) ||
        (c.primaryContact?.email || '').toLowerCase().includes(q)
    );
  }, [clients, search]);

  return (
    <div className="space-y-6" data-testid="customer-success-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <Heart className="w-6 h-6 text-rose-500" />
            Customer Success
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Score d'engagement, alertes proactives et actions recommandées par client.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchAll}
          disabled={loading}
          data-testid="cs-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Rafraîchir
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-4">
        <TabsList data-testid="cs-tabs-list">
          <TabsTrigger value="heart" data-testid="cs-tab-heart">
            <Heart className="w-4 h-4 mr-2 text-rose-500" />
            Heart Score
          </TabsTrigger>
          <TabsTrigger value="nps" data-testid="cs-tab-nps">
            <Activity className="w-4 h-4 mr-2 text-violet-500" />
            Cockpit NPS
          </TabsTrigger>
          <TabsTrigger value="engagement" data-testid="cs-tab-engagement">
            <Activity className="w-4 h-4 mr-2 text-blue-500" />
            Engagement client
          </TabsTrigger>
        </TabsList>

        <TabsContent value="heart" className="space-y-6">

      {/* KPI cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card data-testid="cs-card-total">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 uppercase">Clients suivis</p>
                  <p className="text-3xl font-semibold text-gray-900">{stats.totalClients}</p>
                </div>
                <Users className="w-8 h-8 text-blue-500 opacity-70" />
              </div>
            </CardContent>
          </Card>

          <Card data-testid="cs-card-avg-score">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 uppercase">Score moyen</p>
                  <p className="text-3xl font-semibold text-gray-900">{stats.averageScore}<span className="text-base text-gray-400">/100</span></p>
                </div>
                <Activity className="w-8 h-8 text-emerald-500 opacity-70" />
              </div>
            </CardContent>
          </Card>

          <Card data-testid="cs-card-alerts">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 uppercase">Alertes actives</p>
                  <p className="text-3xl font-semibold text-rose-600">{stats.totalAlertsActive}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-rose-500 opacity-70" />
              </div>
            </CardContent>
          </Card>

          <Card data-testid="cs-card-ca">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 uppercase">CA cumulé 90j</p>
                  <p className="text-3xl font-semibold text-gray-900">{fmtFcfa(stats.totalCa90j)} <span className="text-sm text-gray-400">FCFA</span></p>
                </div>
                <Banknote className="w-8 h-8 text-amber-500 opacity-70" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Phase 2 — Scheduler config card */}
      {schedulerCfg && (
        <Card data-testid="cs-scheduler-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <Settings className="w-4 h-4" /> Recalcul automatique des scores
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Intervalle configurable */}
              <div className="md:col-span-1">
                <label className="text-xs text-gray-500 block mb-1">Intervalle (heures)</label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    min={1}
                    max={24}
                    value={intervalDraft}
                    onChange={(e) => setIntervalDraft(e.target.value)}
                    className="w-20"
                    data-testid="cs-interval-input"
                  />
                  <Button
                    size="sm"
                    onClick={saveSchedulerConfig}
                    disabled={savingCfg || Number(intervalDraft) === schedulerCfg.intervalHours}
                    data-testid="cs-save-interval-btn"
                  >
                    {savingCfg ? '...' : 'Appliquer'}
                  </Button>
                </div>
                <p className="text-[10px] text-gray-400 mt-1">Plage 1-24h (défaut 6h)</p>
              </div>

              {/* Toggle enabled */}
              <div>
                <label className="text-xs text-gray-500 block mb-1">État</label>
                <Button
                  size="sm"
                  variant={schedulerCfg.enabled ? 'default' : 'outline'}
                  onClick={() => toggleSchedulerEnabled(!schedulerCfg.enabled)}
                  disabled={savingCfg}
                  className={schedulerCfg.enabled ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                  data-testid="cs-toggle-enabled-btn"
                >
                  {schedulerCfg.enabled ? (
                    <><CheckCircle2 className="w-4 h-4 mr-1.5" /> Activé</>
                  ) : (
                    <><XCircle className="w-4 h-4 mr-1.5" /> Désactivé</>
                  )}
                </Button>
              </div>

              {/* Last run status */}
              <div>
                <label className="text-xs text-gray-500 block mb-1">Dernier run</label>
                <div className="text-sm">
                  {schedulerCfg.lastRunAt ? (
                    <>
                      <span className={schedulerCfg.lastRunStatus === 'ok' ? 'text-emerald-600' : 'text-rose-600'}>
                        {schedulerCfg.lastRunStatus === 'ok' ? '✓' : '✗'}{' '}
                        {new Date(schedulerCfg.lastRunAt).toLocaleString('fr-FR', {
                          day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
                        })}
                      </span>
                      <div className="text-[10px] text-gray-400">
                        {schedulerCfg.clientsScored ?? 0} clients · {schedulerCfg.lastRunDurationMs ?? 0} ms
                      </div>
                    </>
                  ) : (
                    <span className="text-gray-400">Jamais exécuté</span>
                  )}
                </div>
              </div>

              {/* Run now */}
              <div className="flex items-end">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={runNow}
                  disabled={runningNow}
                  data-testid="cs-run-now-btn"
                  className="w-full"
                >
                  <Play className={`w-4 h-4 mr-1.5 ${runningNow ? 'animate-pulse' : ''}`} />
                  {runningNow ? 'Recalcul...' : 'Recalculer maintenant'}
                </Button>
              </div>
            </div>

            {/* Source indicator */}
            {snapshotAt && source === 'snapshot' && (
              <div className="mt-3 text-[11px] text-gray-400 flex items-center gap-1.5">
                <Clock className="w-3 h-3" />
                Snapshot du {new Date(snapshotAt).toLocaleString('fr-FR', {
                  day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
                })}
                {schedulerCfg.lastRunError && (
                  <span className="text-rose-500 ml-2">— erreur précédente : {schedulerCfg.lastRunError}</span>
                )}
              </div>
            )}
            {source === 'live_fallback' && (
              <div className="mt-3 text-[11px] text-amber-600 flex items-center gap-1.5">
                <AlertTriangle className="w-3 h-3" />
                Aucun snapshot disponible — calcul live (le scheduler générera bientôt un snapshot).
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tier distribution + Alertes par type */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Trophy className="w-4 h-4" /> Répartition par tier
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {Object.entries(stats.tierDistribution || {}).map(([tier, count]) => (
                <div key={tier} className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2">
                    <span>{TIER_STYLES[tier]?.emoji}</span>
                    <span className="capitalize">{tier.replace('_', ' ')}</span>
                  </span>
                  <Badge variant="outline" className={TIER_STYLES[tier]?.color}>
                    {count}
                  </Badge>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-rose-500" /> Alertes par type
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {Object.entries(stats.alertsByType || {}).map(([type, count]) => {
                const meta = ALERT_META[type] || { icon: AlertTriangle, label: type, color: 'text-gray-600 bg-gray-50' };
                const Icon = meta.icon;
                return (
                  <div key={type} className={`flex items-center justify-between p-2 rounded ${meta.color}`}>
                    <span className="flex items-center gap-2 text-sm">
                      <Icon className="w-4 h-4" />
                      {meta.label}
                    </span>
                    <span className="font-semibold">{count}</span>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters + Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-3 mb-4">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Rechercher société ou email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="cs-search-input"
              />
            </div>
            <Select value={tierFilter} onValueChange={setTierFilter}>
              <SelectTrigger className="w-full md:w-48" data-testid="cs-tier-filter">
                <SelectValue placeholder="Tier" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les tiers</SelectItem>
                <SelectItem value="champion">🟢 Champion</SelectItem>
                <SelectItem value="engaged">🔵 Engaged</SelectItem>
                <SelectItem value="at_risk">🟡 At-risk</SelectItem>
                <SelectItem value="dormant">🟠 Dormant</SelectItem>
                <SelectItem value="churning">🔴 Churning</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-full md:w-56" data-testid="cs-sort">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="score_desc"><ArrowUpDown className="w-4 h-4 inline mr-1"/>Score ↓</SelectItem>
                <SelectItem value="score_asc">Score ↑</SelectItem>
                <SelectItem value="alerts_desc">Alertes ↓</SelectItem>
                <SelectItem value="company_asc">Société A→Z</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Table clients */}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Société</TableHead>
                  <TableHead className="w-24">Score</TableHead>
                  <TableHead className="w-28">Tier</TableHead>
                  <TableHead className="w-28">Ops 90j</TableHead>
                  <TableHead className="w-28">CA 90j</TableHead>
                  <TableHead className="w-28">Engagement</TableHead>
                  <TableHead className="w-24">NPS</TableHead>
                  <TableHead className="w-24">Alertes</TableHead>
                  <TableHead>Action recommandée</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredClients.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center text-gray-400 py-8">
                      Aucun client trouvé
                    </TableCell>
                  </TableRow>
                )}
                {filteredClients.map((c) => {
                  const tierStyle = TIER_STYLES[c.tier] || TIER_STYLES.dormant;
                  return (
                    <TableRow key={c.companyName} data-testid={`cs-row-${c.companyName}`}>
                      <TableCell>
                        <div className="font-medium text-gray-900">{c.companyName}</div>
                        <div className="text-xs text-gray-500">{c.primaryContact?.email}</div>
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center font-semibold text-gray-900">
                          {c.score}
                          <span className="text-xs text-gray-400 ml-0.5">/100</span>
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`${tierStyle.color} text-xs`}>
                          {tierStyle.emoji} {c.tierLabel}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">{c.metrics.ops90j}</TableCell>
                      <TableCell className="text-sm">{fmtFcfa(c.metrics.ca90j)}</TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-0.5 text-[11px] text-gray-600">
                          <span className="flex items-center gap-1" title="Dashboard ouvert (90j)">
                            <LayoutDashboard className="w-3 h-3 text-blue-500" />
                            {c.metrics.dashboardOpens90j ?? 0}
                          </span>
                          <span className="flex items-center gap-1" title="Tracking consulté (90j)">
                            <Eye className="w-3 h-3 text-emerald-500" />
                            {c.metrics.trackingViews90j ?? 0}
                          </span>
                          <span className="flex items-center gap-1" title="Documents téléchargés (90j)">
                            <FileDown className="w-3 h-3 text-amber-500" />
                            {c.metrics.documentsDownloaded90j ?? 0}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {c.metrics.lastNps != null ? (
                          <span className={c.metrics.lastNps >= 4 ? 'text-emerald-600' : 'text-rose-600'}>
                            {c.metrics.lastNps}/5
                          </span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {c.alertsCount > 0 ? (
                          <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200">
                            {c.alertsCount}
                          </Badge>
                        ) : (
                          <span className="text-emerald-500 text-xs">✓</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-gray-600">
                        {c.recommendedAction}
                        {c.alerts.length > 0 && (
                          <div className="mt-1 space-y-0.5">
                            {c.alerts.map((a, i) => (
                              <div key={i} className="text-[10px] text-rose-600">
                                • {a.message}
                              </div>
                            ))}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="nps">
          <NpsCockpitPage />
        </TabsContent>

        <TabsContent value="engagement">
          <ClientEngagementTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
